import React, {useState, useEffect, useCallback} from 'react';
import {View, StyleSheet, ScrollView} from 'react-native';
import {Card, Text, Menu, Button, ActivityIndicator} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useReportStore} from '../../store/useReportStore';
import {colors, spacing} from '../../theme';

const ReportInsightsTab = ({orgUnitId}) => {
  const {t} = useTranslation();
  const {insights, isLoading, fetchInsights} = useReportStore();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [menuVisible, setMenuVisible] = useState(false);

  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  const loadData = useCallback(() => {
    fetchInsights(`${year}-01-01`, `${year}-12-31`, orgUnitId);
  }, [year, orgUnitId, fetchInsights]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const renderRankedList = (title, items, valuePrefix = '') => {
    if (!items || items.length === 0) return null;
    return (
      <Card style={styles.sectionCard} mode="elevated">
        <Card.Content>
          <Text style={styles.sectionTitle}>{title}</Text>
          {items.map((item, index) => (
            <View key={index} style={styles.rankedRow}>
              <Text style={styles.rank}>#{index + 1}</Text>
              <Text style={styles.rankedLabel} numberOfLines={1}>{item.label}</Text>
              <Text style={styles.rankedValue}>{valuePrefix}{Number(item.value).toLocaleString('en-IN')}</Text>
            </View>
          ))}
        </Card.Content>
      </Card>
    );
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.yearSelector}>
        <Menu
          visible={menuVisible}
          onDismiss={() => setMenuVisible(false)}
          anchor={
            <Button
              mode="outlined"
              onPress={() => setMenuVisible(true)}
              icon="calendar"
              contentStyle={styles.yearButtonContent}>
              {year}
            </Button>
          }>
          {years.map(y => (
            <Menu.Item
              key={y}
              onPress={() => { setYear(y); setMenuVisible(false); }}
              title={String(y)}
            />
          ))}
        </Menu>
      </View>

      {isLoading ? (
        <ActivityIndicator style={styles.loader} size="large" color={colors.primary} />
      ) : insights ? (
        <>
          {renderRankedList(t('report.topOrderingItems'), insights.topOrderingItems)}
          {renderRankedList(t('report.topCustomers'), insights.topCustomers, '₹')}
          {renderRankedList(t('report.topRevenueDates'), insights.topRevenueDates, '₹')}
          {renderRankedList(t('report.topPerformingDays'), insights.topPerformingDays, '₹')}
        </>
      ) : (
        <Text style={styles.noData}>{t('report.noReportData')}</Text>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  contentContainer: {padding: spacing.md},
  yearSelector: {marginBottom: spacing.md, alignItems: 'flex-start'},
  yearButtonContent: {flexDirection: 'row-reverse'},
  loader: {marginTop: spacing.xl},
  sectionCard: {
    marginBottom: spacing.md,
    backgroundColor: colors.surface,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },
  rankedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs + 2,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  rank: {
    width: 30,
    fontSize: 14,
    fontWeight: '600',
    color: colors.primary,
  },
  rankedLabel: {
    flex: 1,
    fontSize: 14,
    color: colors.text,
  },
  rankedValue: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
  },
  noData: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xl,
  },
});

export default ReportInsightsTab;
